import { ColumnType } from 'antd/lib/table';
import Table from 'antd/lib/table/Table';
import * as React from 'react';
import { OrgComponentProps } from '../../typescriptHelpers';
import Text from 'antd/lib/typography/Text';
import {
    AzureActiveDirectoryTeam,
    AzureTeamTag,
    IntegrationProviderAzureActiveDirectory,
    UserGroup,
    UserGroupsToCommsGroupsMap,
} from '../../types';
import { displayTextForUserGroup } from '../../helpers/displayHelpers';
import {
    selectOrgGroups,
    selectProviderByProviderId,
    selectUserGroupsAndCommsGroupsMapByProviderId,
} from '../../selectors/selectors';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../reducers/reducers';
import { RunwayUserAction } from '../../helpers/aclHelpers';
import { antGrey, antRed, runwayColors } from '../styles/globalStyles';
import PermissionedActionButton from '../HOCs/PermissionedActionButton';
import { CloseOutlined, SettingOutlined } from '@ant-design/icons';
import Space from 'antd/lib/space';
import Divider from 'antd/lib/divider';
import Form from 'antd/lib/form';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import { OrgSettingsUpdatable } from '@/actions/updatable';
import { tryUpdateOrgSettings } from '@/actions/orgSettings';
import { globalMessage } from '@/utils/globalMessage';
import type { CustomTagProps } from 'rc-select/lib/BaseSelect';
import Tag from 'antd/lib/tag';
import Modal from 'antd/lib/modal';
import Select from 'antd/lib/select';

type TeamsTagInfo = {
    team: AzureActiveDirectoryTeam;
    tag: AzureTeamTag;
};

type TeamsTagsDataRow = {
    userGroup: UserGroup;
    teamsTags: string[];
};

interface TeamsTagsModalProps extends TeamsTagsMappingsProps {
    open: boolean;
    onCancel: () => void;
    row?: TeamsTagsDataRow;
    availableTags: TeamsTagInfo[];
}

const TeamsTagsModal: React.FC<TeamsTagsModalProps> = (props: TeamsTagsModalProps) => {
    const [form] = Form.useForm();
    const dispatch = useDispatch();

    const updateUserGroupsToCommsGroupsMapIsLoading = useIsLoading(
        OrgSettingsUpdatable.UpdateProviderIdToUserGroupsToCommsGroupsMap,
    );
    const updateUserGroupsToCommsGroupsMapError = useError(
        OrgSettingsUpdatable.UpdateProviderIdToUserGroupsToCommsGroupsMap,
    );
    const updateUserGroupsToCommsGroupsMapSuccess = useUpdated(
        OrgSettingsUpdatable.UpdateProviderIdToUserGroupsToCommsGroupsMap,
    );

    const existingUserGroupsToCommsGroupsMap = useSelector((state: RootState) =>
        selectUserGroupsAndCommsGroupsMapByProviderId(state, props.providerId, props.orgId),
    );

    const updateUserGroupsToCommsGroupsMap = (values) => {
        if (!props.row?.userGroup || !props.row?.teamsTags) {
            return;
        }

        const userGroupsToCommsGroupsMapChanges: UserGroupsToCommsGroupsMap = {
            [props.row.userGroup.id]: props.availableTags
                .filter((tag) => values.teamsTags.includes(getTeamsTagUIFormat(tag)))
                .map((t) => t.tag.id),
        };

        dispatch(
            tryUpdateOrgSettings(OrgSettingsUpdatable.UpdateProviderIdToUserGroupsToCommsGroupsMap, {
                orgId: props.orgId,
                providerId: props.providerId,
                userGroupsToCommsGroupsMap: {
                    ...existingUserGroupsToCommsGroupsMap,
                    ...userGroupsToCommsGroupsMapChanges,
                },
            }),
        );
    };

    React.useEffect(() => {
        if (updateUserGroupsToCommsGroupsMapError) {
            globalMessage.error(updateUserGroupsToCommsGroupsMapError);
        }
    }, [updateUserGroupsToCommsGroupsMapError]);

    React.useEffect(() => {
        if (props.open && updateUserGroupsToCommsGroupsMapSuccess) {
            globalMessage.success(`Teams tag for ${displayTextForUserGroup(props.row?.userGroup)} user group updated!`);

            props.onCancel();
        }
    }, [props.open, updateUserGroupsToCommsGroupsMapSuccess, props.row]);

    const selectOptions = sortAndValidTeamsTagInfo(props.availableTags);
    const tagRender: ({ value, closable }: CustomTagProps) => React.ReactElement = ({
        value,
        closable,
        onClose,
    }: CustomTagProps) => {
        const existingGroup = selectOptions.find((group) => getTeamsTagUIFormat(group) === value);
        const tagLabel = existingGroup != null ? getTeamsTagUIFormat(existingGroup) : `${value} (notFound)`;

        return (
            <Tag
                color={existingGroup != null ? antGrey.grey3 : antRed.red3}
                closable={closable}
                closeIcon={<CloseOutlined style={{ color: antGrey.grey7 }} />}
                style={{ marginRight: 3, color: runwayColors.black }}
                onClose={onClose}
            >
                {tagLabel}
            </Tag>
        );
    };

    const initialValue = (props.row?.teamsTags ?? [])
        .map((t) => getTeamsTagInfo(props.availableTags, t))
        .map((t) => getTeamsTagUIFormat(t));

    return (
        <Modal
            title={`${displayTextForUserGroup(props.row?.userGroup)} group Teams tag`}
            okButtonProps={{
                loading: updateUserGroupsToCommsGroupsMapIsLoading,
            }}
            onOk={() => form.submit()}
            okText="Save"
            open={props.open}
            onCancel={() => props.onCancel()}
            destroyOnClose
        >
            <div style={{ display: 'flex', flexDirection: 'column' }}>
                <Form
                    onFinish={updateUserGroupsToCommsGroupsMap}
                    initialValues={{
                        teamsTags: initialValue,
                    }}
                    form={form}
                    preserve={false}
                    layout="vertical"
                >
                    <Form.Item
                        name="teamsTags"
                        label="Teams tag(s)"
                        extra="You can manage the new group's membership from Teams at any time."
                        rules={[
                            {
                                validator: (_, value) => {
                                    return validateTeamsTag(value);
                                },
                            },
                        ]}
                    >
                        <Select mode="tags" style={{ width: '100%' }} placeholder="Teams tag(s)" tagRender={tagRender}>
                            {selectOptions.map((option) => (
                                <Select.Option key={getTeamsTagUIFormat(option)}>
                                    {getTeamsTagUIFormat(option)}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Form>
            </div>
        </Modal>
    );
};

interface TeamsTagsMappingsProps extends OrgComponentProps {
    providerId: string;
}

export const TeamsTagsMappingsTable: React.FC<TeamsTagsMappingsProps> = (props: TeamsTagsMappingsProps) => {
    const teamsTagsMap = useSelector((state: RootState) =>
        selectUserGroupsAndCommsGroupsMapByProviderId(state, props.providerId, props.orgId),
    );

    const orgGroupsById = useSelector((state: RootState) => selectOrgGroups(state, props.orgId));

    const notificationsProvider = useSelector(
        (state: RootState) =>
            selectProviderByProviderId(state, props.orgId, props.providerId) as IntegrationProviderAzureActiveDirectory,
    );

    const availableTagsMap: Record<string, AzureTeamTag[]> = notificationsProvider?.data?.availableTags ?? {};
    const availableTeams = notificationsProvider?.data?.availableTeams ?? [];
    const availableTags: TeamsTagInfo[] = [];
    for (const key of Object.keys(availableTagsMap)) {
        for (const tag of availableTagsMap[key]) {
            const team = availableTeams.find((t) => t.id == key);
            const obj = {
                team: team,
                tag: tag,
            };

            availableTags.push(obj);
        }
    }

    const teamsTagsData: TeamsTagsDataRow[] = Object.entries(teamsTagsMap ?? {}) // in case teamsTagsMap is null, pass empty object to prevent crash
        .sort()
        .map(([userGroupId, teamsTags]) => ({
            userGroup: orgGroupsById[userGroupId],
            teamsTags: teamsTags ?? [],
        }));

    const [selectedRow, setSelectedRow] = React.useState<TeamsTagsDataRow>(null);

    const renderColumnTitle = (title: string) => {
        return <Text strong>{title}</Text>;
    };

    const renderTeamsTag = (row: TeamsTagsDataRow) => {
        const tags = availableTags.filter((tag) => row.teamsTags.includes(tag.tag.id));
        const teamsTagsString = tags.map((group) => getTeamsTagUIFormat(group)).join(', ') ?? '';

        return (
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flex: 1,
                }}
            >
                {!teamsTagsString ?
                    <Text type="secondary">Not set</Text>
                :   <Text>{teamsTagsString}</Text>}
                <PermissionedActionButton
                    actionType={RunwayUserAction.UAUpdateOrgSettings}
                    onClick={() => {
                        setSelectedRow(row);
                    }}
                    icon={<SettingOutlined style={{ color: antGrey.grey8 }} />}
                    ghost
                    disabled={false}
                />
            </div>
        );
    };

    const columns: ColumnType<TeamsTagsDataRow>[] = [
        {
            title: renderColumnTitle('Runway group'),
            dataIndex: 'userGroup',

            render: (userGroup: UserGroup) => {
                return <Text>{displayTextForUserGroup(userGroup)}</Text>;
            },
            align: 'left',
            width: '50%',
        },
        {
            title: renderColumnTitle('Teams tag(s)'),
            render: renderTeamsTag,
            align: 'left',
            width: '50%',
        },
    ];

    return !teamsTagsMap ? null : (
            <>
                <div
                    style={{
                        display: 'flex',
                        gap: 20,
                        alignItems: 'center',
                    }}
                >
                    <Text
                        style={{
                            fontSize: 20,
                            color: antGrey.grey7,
                            whiteSpace: 'nowrap',
                        }}
                    >
                        Microsoft Teams Tags
                    </Text>
                    <div
                        style={{
                            flex: 1,
                            height: '100%',
                        }}
                    >
                        <Divider
                            type="horizontal"
                            style={{
                                width: '100%',
                                margin: 0,
                            }}
                        />
                    </div>
                </div>
                <Space direction="vertical" size={16} style={{ width: '100%', marginTop: 15 }}>
                    <Space>
                        <Text type="secondary">
                            Configure the Teams tag(s) that correspond to each Runway user group.
                        </Text>
                    </Space>
                    <Table
                        size="middle"
                        columns={columns}
                        dataSource={teamsTagsData}
                        pagination={{ hideOnSinglePage: true, defaultPageSize: 5, size: 'default' }}
                    />
                    <TeamsTagsModal
                        open={!!selectedRow}
                        providerId={props.providerId}
                        row={selectedRow}
                        availableTags={availableTags}
                        orgId={props.orgId}
                        onCancel={() => setSelectedRow(null)}
                    />
                </Space>
            </>
        );
};

const getTeamsTagUIFormat: (tagInfo: TeamsTagInfo) => string = (tagInfo) => {
    if (!isValidTagInfo(tagInfo)) {
        return '';
    }

    const teamsTag = tagInfo.tag.displayName;
    return `${tagInfo.team.name} / ${teamsTag[0] == '@' ? teamsTag : `@${teamsTag}`}`;
};

const getTeamsTagInfo: (tagInfos: TeamsTagInfo[], identifier: string) => TeamsTagInfo = (tagInfos, identifier) => {
    return tagInfos.find((tagInfo) => tagInfo.tag.id == identifier || getTeamsTagUIFormat(tagInfo) == identifier);
};

const isValidTagInfo: (tagInfo: TeamsTagInfo) => boolean = (tagInfo) => {
    return (
        tagInfo &&
        tagInfo.team &&
        tagInfo.tag &&
        tagInfo.team.name.trim().length > 0 &&
        tagInfo.tag.displayName.trim().length > 0
    );
};

const sortAndValidTeamsTagInfo: (tagInfos: TeamsTagInfo[]) => TeamsTagInfo[] = (tagInfos) => {
    return tagInfos
        .sort((a, b) => {
            if (a.tag.displayName < b.tag.displayName) {
                return -1;
            }
            if (a.tag.displayName > b.tag.displayName) {
                return 1;
            }
            return 0;
        })
        .filter((tag) => isValidTagInfo(tag));
};

const validateTeamsTag = (value: string[]) => {
    const tags = value ?? [];
    const invalidTeamsTagHandles = [];
    const regex = /^[A-z0-9.\-_@\/\s]+$/;

    for (const teamsTagHandle of tags) {
        if (!regex.test(teamsTagHandle)) {
            invalidTeamsTagHandles.push(teamsTagHandle);
        }
    }

    if (invalidTeamsTagHandles.length > 0) {
        return Promise.reject(`Invalid character(s) in Teams tag handle(s): ${invalidTeamsTagHandles.join(', ')}`);
    }

    return Promise.resolve();
};
